import { useEffect, useState } from "react";
import { Loader2, TrendingUp, Users, Mail, CreditCard, DollarSign, Send, Lock, Download, Eye, MousePointerClick } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

function MetricCard({ icon: Icon, label, value, sub, tone = "amber", testid }) {
  const tones = {
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    stone: "bg-stone-50 text-stone-700 border-stone-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm" data-testid={testid}>
      <div className={`inline-flex items-center justify-center w-9 h-9 rounded-xl border ${tones[tone]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-4 text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold">{label}</div>
      <div className="mt-1 font-display text-3xl font-bold text-stone-900 tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-sm text-stone-500">{sub}</div>}
    </div>
  );
}

function TrendCard({ title, data, stroke, testid, days }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-stone-900">{title}</h3>
        <span className="text-xs text-stone-500">Last {days} days</span>
      </div>
      <div style={{ width: "100%", height: 180 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F5F5F4" />
            <XAxis dataKey="date" hide />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#A8A29E" }} width={30} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 12, border: "1px solid #E7E5E4" }}
              labelFormatter={(l) => new Date(l).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            />
            <Line type="monotone" dataKey="count" stroke={stroke} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function Growth() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [days, setDays] = useState(30);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRefreshing(true);
      try {
        const res = await api.get(`/dashboard/growth?days=${days}`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || "Failed to load growth stats");
      } finally {
        if (!cancelled) setRefreshing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  if (user && user.role !== "admin") {
    return (
      <div className="max-w-md mx-auto mt-20 bg-white border border-stone-200 rounded-3xl p-10 text-center" data-testid="growth-forbidden">
        <Lock className="w-10 h-10 text-stone-400 mx-auto" />
        <h1 className="mt-4 font-display text-2xl font-semibold">Admin only</h1>
        <p className="mt-2 text-stone-500 text-sm">Growth analytics is limited to admin accounts.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3" data-testid="growth-error">
        {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-stone-500" data-testid="growth-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading growth stats…
      </div>
    );
  }

  const { leads, users, subs, revenue, funnel, trends } = data;

  const exportCsv = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/dashboard/leads.csv`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `homeschoolhub-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Leads CSV downloaded");
    } catch {
      toast.error("Export failed — please try again");
    }
  };

  return (
    <div className="space-y-8" data-testid="growth-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">Admin analytics</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight">Growth</h1>
          <p className="mt-2 text-stone-500 text-sm">
            Lead capture, conversion, and revenue at a glance — updated live.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Range picker */}
          <div className="inline-flex rounded-full bg-white border border-stone-200 p-1" data-testid="growth-range-picker">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  days === r.days ? "bg-stone-900 text-white" : "text-stone-600 hover:text-stone-900"
                }`}
                data-testid={`range-${r.days}d`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {refreshing && <Loader2 className="w-4 h-4 text-stone-400 animate-spin" data-testid="growth-refreshing" />}
          <Button
            onClick={exportCsv}
            className="rounded-full bg-stone-900 hover:bg-stone-800 text-white gap-2"
            data-testid="growth-export-csv"
          >
            <Download className="w-4 h-4" />
            Export leads (CSV)
          </Button>
        </div>
      </div>

      {/* HERO METRICS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          testid="metric-mrr"
          icon={DollarSign}
          label="MRR"
          value={`$${subs.mrr.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          sub={`ARR $${subs.arr.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          tone="emerald"
        />
        <MetricCard
          testid="metric-active-subs"
          icon={CreditCard}
          label="Active subs"
          value={subs.active}
          sub={`Basic ${subs.by_tier.basic || 0} · Plus ${subs.by_tier.plus || 0} · Premium ${subs.by_tier.premium || 0}`}
          tone="amber"
        />
        <MetricCard
          testid="metric-users"
          icon={Users}
          label="Teachers"
          value={users.total}
          sub={`+${users.period} in last ${days}d`}
          tone="stone"
        />
        <MetricCard
          testid="metric-leads"
          icon={Mail}
          label="Leads"
          value={leads.total}
          sub={`+${leads.period} in last ${days}d`}
          tone="orange"
        />
      </div>

      {/* SECONDARY */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          testid="metric-drip-sent"
          icon={Send}
          label="Drips sent"
          value={leads.drip3_sent + leads.drip7_sent}
          sub={`Day-3: ${leads.drip3_sent} · Day-7: ${leads.drip7_sent}`}
          tone="stone"
        />
        <MetricCard
          testid="metric-open-rate"
          icon={Eye}
          label="Open rate"
          value={`${leads.open_rate_pct}%`}
          sub={`${leads.opens} opens${leads.bounces ? ` · ${leads.bounces} bounces` : ""}`}
          tone="emerald"
        />
        <MetricCard
          testid="metric-click-rate"
          icon={MousePointerClick}
          label="Click rate"
          value={`${leads.click_rate_pct}%`}
          sub={`${leads.clicks} clicks`}
          tone="rose"
        />
        <MetricCard
          testid="metric-revenue-period"
          icon={DollarSign}
          label={`Revenue (${days}d)`}
          value={`$${revenue.period.toFixed(2)}`}
          sub={`${revenue.paid_txn_period} paid txn`}
          tone="emerald"
        />
      </div>

      {/* Conversion band */}
      <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
        <MetricCard
          testid="metric-conversion"
          icon={TrendingUp}
          label="Lead → signup"
          value={`${funnel.conversion_pct}%`}
          sub={`${funnel.leads_signed_up} of ${funnel.leads} leads`}
          tone="amber"
        />
        <MetricCard
          testid="metric-drip-pending"
          icon={Send}
          label="Drip queue"
          value={leads.drip_pending}
          sub="Leads awaiting day-3 send"
          tone="orange"
        />
      </div>

      {/* TRENDS */}
      <div className="grid md:grid-cols-2 gap-4">
        <TrendCard testid="trend-leads" title="Leads captured" data={trends.leads} stroke="#F59E0B" days={trends.days} />
        <TrendCard testid="trend-signups" title="Teacher signups" data={trends.signups} stroke="#10B981" days={trends.days} />
      </div>

      {/* FUNNEL */}
      <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm" data-testid="growth-funnel">
        <h3 className="font-display font-semibold text-stone-900">Conversion funnel</h3>
        <p className="mt-1 text-xs text-stone-500">Lead → registered teacher → active paying subscription</p>
        <div className="mt-6 grid grid-cols-3 gap-3 items-end">
          {[
            { label: "Leads captured", value: funnel.leads, tone: "bg-amber-500" },
            { label: "Registered teachers", value: funnel.leads_signed_up, tone: "bg-orange-500" },
            { label: "Active subscribers", value: subs.active, tone: "bg-emerald-500" },
          ].map((step, i) => {
            const max = Math.max(funnel.leads, funnel.leads_signed_up, subs.active, 1);
            const pct = Math.max(6, (step.value / max) * 100);
            return (
              <div key={step.label} className="text-center">
                <div className={`mx-auto ${step.tone} rounded-t-2xl transition-all`} style={{ width: "80%", height: `${pct * 1.6}px` }} />
                <div className="mt-3 font-display text-2xl font-bold tabular-nums">{step.value}</div>
                <div className="text-xs text-stone-500">{step.label}</div>
                {i > 0 && (
                  <div className="mt-1 text-[10px] uppercase tracking-[0.15em] font-semibold text-stone-400">
                    {step.value > 0 && funnel.leads > 0 ? `${((step.value / funnel.leads) * 100).toFixed(1)}% of leads` : "—"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
